import copy from 'copy-to-clipboard'
import { APP_CONFIG } from 'crypto-otc/project-config'
import { useI18n } from 'i18n-utils'
import { PLATFORM_CONFIG } from 'platform-config'
import queryString from 'query-string'
import { useBaseInfoStore, useEnvStore, useResponsive } from 'store'
import { StorageIndexDBEnum } from 'store/src/storage/StorageDB'
import { UiUtils } from 'ui-components'

type DownloadType = 'ANDROID' | 'IOS' | 'GOOGLE' | 'TESTFLIGHT' | 'DEF'

export const useDownloadConfig = () => {
  const envStore = useEnvStore()

  const qrState = ref({
    url: !envStore.isEmptyConfig ? envStore.config.qrcodeDownload.oriUrl : '',
    type: 'DEF',
  })

  const baseInfoStore = useBaseInfoStore()
  const { t } = useI18n()
  const storage = window.sessionStorage
  const inviteCodeKey = `${import.meta.env.VITE_PLATFORM}__${StorageIndexDBEnum.InviteCode}`
  const scenesKey = `${import.meta.env.VITE_PLATFORM}__${StorageIndexDBEnum.Scenes}`
  const productIdKey = `${import.meta.env.VITE_PLATFORM}__${StorageIndexDBEnum.ProductId}`
  const responsive = useResponsive()

  const inviteCode = computed(() => {
    const params = queryString.parse(window.location.search)
    const code = (params.ref || params.invite_code || storage.getItem(inviteCodeKey) || '') as string
    if (!code || +code === 0) return ''
    return code
  })

  const copyInviteCode = () => {
    const inviteCode = storage.getItem(inviteCodeKey) || ''
    const scenes = storage.getItem(scenesKey) || '0'
    const productId = storage.getItem(productIdKey) || '0'
    if (!inviteCode || +inviteCode === 0) return
    copy(
      JSON.stringify({
        invite_code: inviteCode,
        scenes,
        product_id: productId,
      }),
    )
  }

  const handleDownload = (url?: string, type: DownloadType = 'DEF') => {
    copyInviteCode()

    if (APP_CONFIG.isReport) {
      window?.gtag('event', 'app_click_download', {
        source: String(type).toLowerCase(),
      })
    }

    if (!url) return

    if (responsive.isMobile) {
      window.open(url, '_blank')
      return
    }

    qrState.value = { url, type }
  }

  const onDownloadApp = () => {
    if (envStore.isEmptyConfig) return
    const url = envStore.config.qrcodeDownload.oriUrl
    handleDownload(url)
  }

  const onGoToDownloadAndroid = () => {
    if (envStore.isEmptyConfig) return
    const url = envStore.config?.downloadAppInfo?.defultApkUrl || baseInfoStore.webInitInfo?.androidUrl

    handleDownload(url, 'ANDROID')
  }

  const onGoToDownloadGooglePlay = () => {
    if (envStore.isEmptyConfig) return
    const url = envStore.config?.downloadAppInfo?.defultGooglePlay || baseInfoStore.webInitInfo?.googlePlay

    handleDownload(url, 'GOOGLE')
  }

  const isShowAppStore = computed(() => {
    const appStore = PLATFORM_CONFIG.home.supportPlatform.appStore
    if (appStore === undefined || appStore === null) return true
    return appStore
  })

  const onGoToDownloadIos = () => {
    if (!isShowAppStore.value) {
      return UiUtils.showModal(t('敬请期待'), {
        placement: 'center',
      })
    }

    const url = envStore.config?.downloadAppInfo?.defultAppstoreUrl || baseInfoStore.webInitInfo?.appstoreUrl

    handleDownload(url, 'IOS')
  }

  const onGoToDownloadTestFlight = () => {
    const url = envStore.config?.downloadAppInfo?.defultTestFlightUrl || baseInfoStore.webInitInfo?.testFlightUrl

    handleDownload(url, 'TESTFLIGHT')
  }

  onMounted(() => {
    nextTick(() => {
      const params = queryString.parse(window.location.search)
      const inviteCode = (params.ref || params.invite_code || storage.getItem(inviteCodeKey) || '') as string
      storage.setItem(inviteCodeKey, inviteCode)

      const scenes = (params.scenes || storage.getItem(scenesKey) || '') as string
      storage.setItem(scenesKey, scenes)

      const productId = (params.product_id || storage.getItem(productIdKey) || '') as string
      storage.setItem(productIdKey, productId)
    })
  })
  return {
    qrState,
    inviteCode,
    onDownloadApp,
    onGoToDownloadIos,
    onGoToDownloadAndroid,
    onGoToDownloadGooglePlay,
    onGoToDownloadTestFlight,
  }
}
